import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'

import { date, type Dayjs } from 'configuration/dayjs'
import { REGION_LABELS } from 'const/codes'
import {
    ADVANCED_TEXT,
    BRANDS,
    DATES,
    DSP_ADVERTISERS,
    DSP_AUDIENCE_ID,
    DSP_AUDIENCE_NAME,
    DSP_COUNTRIES,
    DSP_CREATIVE_ID,
    DSP_CREATIVE_NAME,
    DSP_CREATIVE_TYPE,
    DSP_LINE_ITEMS,
    DSP_ORDERS,
    DSP_SUPPLY_SOURCE,
    DSP_TARGETING_METHOD,
    LABELS,
    METRIC_FILTERS,
} from 'const/filters'
import { DSP_ORDER_LABELS } from 'const/resourceTypes'
import { formatMetricFiltersForParams } from 'helpers/filters/metrics'
import { formatCerebroDate } from 'helpers/formatting'
import type { FilterOption, FiltersState } from 'types'

import { presetRanges } from '../dateRange'
import { formatBooleanFilter } from '../filters'
import { isNonEmptyArray } from '../typeGuard'

export const formatFilters = (
    filters: FiltersState,
    _hasHourlyData = false,
    fiscalYearStartMonth?: number
): Record<string, string | number | boolean> => {
    let params: Record<string, string | number | boolean> = {}

    const handleIdUpdateForRegions = (item: FilterOption): string => {
        const regionCodes = Object.keys(REGION_LABELS).map((code) =>
            code.slice(0, 2)
        )

        const valueAsString = String(item.value)
        const startsWithRegionCode = regionCodes.some((code) =>
            valueAsString.startsWith(code)
        )

        return startsWithRegionCode
            ? String(item.value)
            : regionCodes.map((code) => `${code}${valueAsString}`).join(',')
    }

    const stringifyFilterOptions = (options: FilterOption[]): string =>
        options.map((item) => item.value).join()

    const updateCountryFilter = (
        selectedDspCountries: FilterOption[]
    ): FilterOption[] => {
        return [
            ...selectedDspCountries,
            ...(selectedDspCountries.some((country) => country.value === 'UK')
                ? [{ value: 'GB', label: 'United Kingdom' }]
                : []),
        ]
    }

    if (!isEmpty(filters[DATES])) {
        if (isArray(filters[DATES])) {
            const dates = filters[DATES]
            params.report_date_min = formatCerebroDate(dates[0])
            params.report_date_max = formatCerebroDate(dates[1])
        } else {
            const presetMap = presetRanges(
                date(),
                filters.rangeLag,
                fiscalYearStartMonth
            ) as Record<string, { range: [Dayjs, Dayjs] }>
            const preset = presetMap[filters[DATES] as string]
            if (!preset) {
                return params
            }
            const { range } = preset

            params.report_date_min = formatCerebroDate(range[0])
            params.report_date_max = formatCerebroDate(range[1])
        }
    }

    if (!isEmpty(filters[BRANDS])) {
        const items = filters[BRANDS]!
        params.profile_id__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[DSP_ADVERTISERS])) {
        const items = filters[DSP_ADVERTISERS]!
        params.advertiser_id__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[DSP_ORDERS])) {
        const items = filters[DSP_ORDERS]!
        params.order_id__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[DSP_LINE_ITEMS])) {
        const items = filters[DSP_LINE_ITEMS]!
        params.line_item_id__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[DSP_AUDIENCE_NAME])) {
        const items = filters[DSP_AUDIENCE_NAME]!
        params.segment_name__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[DSP_AUDIENCE_ID])) {
        const items = filters[DSP_AUDIENCE_ID]!
        params.segment_id__in = items
            .map((item) => handleIdUpdateForRegions(item))
            .join()
    }

    const booleanFilterGroups: Record<string, string | boolean>[][] = []

    const dspSupplySource = filters[DSP_SUPPLY_SOURCE]
    if (isNonEmptyArray(dspSupplySource)) {
        const fieldId =
            dspSupplySource.find((item) => item.metadata)?.metadata ??
            'supply_source_name'
        const knownValues = dspSupplySource
            .filter((item) => item.value !== 'unknown')
            .map((item) => String(item.value))
        const includeUnknown = dspSupplySource.some(
            (item) => item.value === 'unknown'
        )

        booleanFilterGroups.push([
            ...knownValues.map((value) => ({
                [fieldId]: value,
            })),
            ...(includeUnknown ? [{ [`${fieldId}__isnull`]: true }] : []),
        ])
        if (!includeUnknown) {
            params[`${fieldId}__isnotnull`] = true
        }
    }

    if (!isEmpty(filters[DSP_CREATIVE_NAME])) {
        const items = filters[DSP_CREATIVE_NAME]!
        params.creative_name__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[DSP_CREATIVE_ID])) {
        const items = filters[DSP_CREATIVE_ID]!
        params.creative_id__in = items
            .map((item) => handleIdUpdateForRegions(item))
            .join()
    }

    const labels = filters[LABELS]
    if (isNonEmptyArray(labels)) {
        params.complex_label_filter = formatBooleanFilter(
            labels,
            DSP_ORDER_LABELS
        )
    }

    const dspTargetingMethod = filters[DSP_TARGETING_METHOD]
    if (isNonEmptyArray(dspTargetingMethod)) {
        const knownValues = dspTargetingMethod
            .filter((item) => item.value !== 'unknown')
            .map((item) => String(item.value))
        const includeUnknown = dspTargetingMethod.some(
            (item) => item.value === 'unknown'
        )

        if (includeUnknown) {
            booleanFilterGroups.push([
                ...knownValues.map((value) => ({
                    targeting_method: value,
                })),
                { targeting_method__isnull: true },
            ])
        } else {
            params.targeting_method__in = knownValues.join()
        }
    }

    const dspCreativeType = filters[DSP_CREATIVE_TYPE]
    if (isNonEmptyArray(dspCreativeType)) {
        const creativeTypeClauses: Record<string, string | boolean>[] = []
        dspCreativeType.forEach((item) => {
            if (item.value === 'unknown') {
                creativeTypeClauses.push({ creative_type__isnull: true })
            } else {
                creativeTypeClauses.push({
                    creative_type__icontains: String(item?.value ?? ''),
                })
            }
        })
        booleanFilterGroups.push(creativeTypeClauses)
    }

    const populatedBooleanFilterGroups = booleanFilterGroups.filter(
        (group) => group.length > 0
    )
    if (populatedBooleanFilterGroups.length === 1) {
        params.boolean_filter = JSON.stringify({
            or: populatedBooleanFilterGroups[0],
        })
    } else if (populatedBooleanFilterGroups.length > 1) {
        params.boolean_filter = JSON.stringify({
            and: populatedBooleanFilterGroups.map((or) => ({ or })),
        })
    }

    const dspAdvancedText = filters[ADVANCED_TEXT]
    if (isNonEmptyArray(dspAdvancedText)) {
        params.complex_text_filter = formatBooleanFilter(dspAdvancedText)
    }

    const dspCountries = filters[DSP_COUNTRIES]
    if (isNonEmptyArray(dspCountries)) {
        const updatedCountries = updateCountryFilter(filters[DSP_COUNTRIES]!)
        params.advertiser__country_code__in =
            stringifyFilterOptions(updatedCountries)
    }

    const metricFilters = filters[METRIC_FILTERS]
    if (isNonEmptyArray(metricFilters)) {
        params = {
            ...params,
            ...formatMetricFiltersForParams(metricFilters),
        } as Record<string, string | number | boolean>
    }

    return params
}
